/**
 * Toolbar-driven expand / collapse-all for the Claim Owner capacity band.
 *
 * NO NEW REFRESH PATH. Both controls go through Bryntum's own
 * `RowExpander#expand` / `#collapse`, so SchedulerCore's in-place
 * `refreshCapacityBands` (wired to `rowExpand` / `timeAxisChange`) does the
 * rendering, unchanged. RowExpander caches its body element per record, so a
 * collapse/re-expand is NOT a refresh and must never be used as one.
 */

/** Minimal record shape. Records are passed through to Bryntum untouched. */
export interface CapacityExpanderRecord {
  id: string | number;
}

export interface CapacityRowExpanderFeature {
  /**
   * `true` whenever the tenant flag or the user's capacity toggle is off —
   * SchedulerCore flips this on the live instance. An expand is a no-op then,
   * and Bryntum's own `doDisable` has already cleared the expanded records.
   */
  disabled?: boolean;
  /**
   * Bryntum's per-record expanded-state map — the ONLY place the set of
   * currently expanded rows exists. RowExpander 7.2.0 exposes no public
   * `expandedRecords` / `isExpanded` / `collapseAll`, so collapse-all has to
   * read this. Optional here so a version that renames it degrades to "nothing
   * to collapse" instead of throwing.
   */
  recordStateMap?: Map<CapacityExpanderRecord, unknown>;
  expand(record: CapacityExpanderRecord): unknown;
  collapse(record: CapacityExpanderRecord): unknown;
}

/** The slice of the live Bryntum instance these controls touch. */
export interface CapacityExpansionScheduler {
  features?: { rowExpander?: CapacityRowExpanderFeature };
  /** Filtered, currently visible resource rows — "every resource listed". */
  resourceStore?: { records?: readonly CapacityExpanderRecord[] };
  /** Row selection (`selectionMode.row` + `multiSelect`), so resource records. */
  selectedRecords?: readonly CapacityExpanderRecord[];
}

/**
 * Open a strip for every selected resource, or — with nothing selected — for
 * every resource currently listed.
 *
 * EXPANSION DOES NOT FOLLOW SELECTION. The selection is read once, here, at
 * click time. The board replaces the selection on a plain click, so a
 * dispatcher working the board changes it constantly; if expansion tracked it,
 * every click would slam strips shut. After this call each row holds its own
 * state until the dispatcher collapses it — nothing in this module ever
 * collapses a row because it left the selection.
 */
export function expandCapacityBands(
  scheduler: CapacityExpansionScheduler | null | undefined
): void {
  const rowExpander = scheduler?.features?.rowExpander;
  // Disabled means flag-off or toggle-off. The controls are not rendered then,
  // so this is belt-and-braces: `expand()` would no-op anyway.
  if (!rowExpander || rowExpander.disabled) return;

  const selected = scheduler?.selectedRecords ?? [];
  const targets =
    selected.length > 0 ? selected : scheduler?.resourceStore?.records ?? [];

  // Deliberately NOT awaited. `expand()`'s promise resolves only once the row
  // is rendered into view, which for a row scrolled out of view can be much
  // later or never — awaiting 40 of them would hang the handler.
  targets.forEach((record) => {
    rowExpander.expand(record);
  });
}

/**
 * Close every open strip in one action.
 *
 * This is the stated mitigation for the band's one real cost: each expanded
 * strip inserts rows between one resource's task row and the next, so opening
 * many resources lengthens the path a drag has to travel across the board.
 * Strips must never be the only way back to a dense board — which is why this
 * control is paired with Expand rather than left to per-row collapsing.
 *
 * The expanded set is snapshotted first because `collapse()` deletes from the
 * very map being iterated. Collapsing only the open rows is also deliberate —
 * `collapse()` does not early-return for a closed row, so looping the whole
 * store would fire a spurious `rowCollapse` and a re-render per resource.
 */
export function collapseAllCapacityBands(
  scheduler: CapacityExpansionScheduler | null | undefined
): number {
  const rowExpander = scheduler?.features?.rowExpander;
  if (!rowExpander) return 0;

  const expanded = Array.from(rowExpander.recordStateMap?.keys() ?? []);
  expanded.forEach((record) => {
    rowExpander.collapse(record);
  });
  return expanded.length;
}
